import type { OfficeGoogleAuthPayload, OfficeGoogleAuthSuccessPayload } from '@/office/auth/types';

const OFFICE_AUTH_CHANGED_EVENT = 'contract-insights.office-auth-changed';
const OFFICE_AUTH_TOKEN_STORAGE_KEY = 'contract-insights.office-auth-token';
const GOOGLE_AUTH_DIALOG_PATH = '/office/auth/start.html';
const OFFICE_DIALOG_OPEN_TIMEOUT_MS = 30_000;

type OfficeDialogHandle = {
  addEventHandler: (eventType: unknown, handler: (arg: unknown) => void) => void;
  close: () => void;
};

type OfficeDialogAsyncResult = {
  error: {
    message: string;
  };
  status: unknown;
  value: OfficeDialogHandle;
};

export function isOfficeAddinHost(): boolean {
  return typeof window !== 'undefined' && Boolean(window.Office?.context?.ui);
}

export function getStoredOfficeAuthToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(OFFICE_AUTH_TOKEN_STORAGE_KEY);
}

export function setStoredOfficeAuthToken(token: string): void {
  window.localStorage.setItem(OFFICE_AUTH_TOKEN_STORAGE_KEY, token);
}

export function clearStoredOfficeAuthToken(): void {
  window.localStorage.removeItem(OFFICE_AUTH_TOKEN_STORAGE_KEY);
}

export function dispatchOfficeAuthChanged(): void {
  window.dispatchEvent(new Event(OFFICE_AUTH_CHANGED_EVENT));
}

export function getOfficeAuthChangedEventName(): string {
  return OFFICE_AUTH_CHANGED_EVENT;
}

async function openOfficeGoogleLoginDialog(): Promise<OfficeGoogleAuthSuccessPayload> {
  const office = window.Office;
  if (!office?.onReady) {
    throw new Error('Office dialog API is not available in this host.');
  }

  await office.onReady();

  const ui = office.context?.ui;
  const asyncResultStatus = office.AsyncResultStatus;
  const eventType = office.EventType;
  if (!ui?.displayDialogAsync || !asyncResultStatus || !eventType) {
    throw new Error('Office dialog API is not available in this host.');
  }

  const displayDialogAsync = ui.displayDialogAsync;

  const dialogUrl = `${window.location.origin}${GOOGLE_AUTH_DIALOG_PATH}`;

  const dialog = await new Promise<OfficeDialogHandle>((resolve, reject) => {
    let settled = false;
    let timedOut = false;

    const openTimeout = window.setTimeout(() => {
      timedOut = true;
      rejectDialog(new Error('The Google login dialog did not open in time.'));
    }, OFFICE_DIALOG_OPEN_TIMEOUT_MS);

    const resolveDialog = (dialogHandle: OfficeDialogHandle): void => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(openTimeout);
      resolve(dialogHandle);
    };

    const rejectDialog = (error: Error): void => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(openTimeout);
      reject(error);
    };

    try {
      displayDialogAsync(
        dialogUrl,
        {
          displayInIframe: false,
          height: 72,
          promptBeforeOpen: false,
          width: 42,
        },
        (result: OfficeDialogAsyncResult) => {
          if (result.status === asyncResultStatus.Succeeded) {
            if (timedOut) {
              try {
                result.value.close();
              } catch {
                // noop
              }
              return;
            }

            resolveDialog(result.value);
            return;
          }

          rejectDialog(new Error(result.error.message));
        }
      );
    } catch (error) {
      rejectDialog(
        error instanceof Error ? error : new Error('The Google login dialog could not be opened.')
      );
    }
  });

  return new Promise<OfficeGoogleAuthSuccessPayload>((resolve, reject) => {
    const closeWithError = (message: string): void => {
      try {
        dialog.close();
      } catch {
        // noop
      }

      reject(new Error(message));
    };

    dialog.addEventHandler(eventType.DialogMessageReceived, (arg: unknown) => {
      const messageArg = arg as { message: string };

      try {
        const payload = JSON.parse(messageArg.message) as OfficeGoogleAuthPayload;

        if (payload.type === 'auth-success') {
          dialog.close();
          resolve(payload);
          return;
        }

        const errorMessage = [
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
          payload.message || 'Google sign-in failed.',
          ...(payload.details ?? []),
        ].join('\n');
        closeWithError(errorMessage);
      } catch {
        closeWithError('Google sign-in returned an invalid response.');
      }
    });

    dialog.addEventHandler(eventType.DialogEventReceived, (arg: unknown) => {
      const eventArg = arg as { error: number };
      const errorMessageByCode: Record<number, string> = {
        12002: 'The Google login dialog could not be loaded.',
        12003: 'The Google login dialog was redirected to an invalid URL.',
        12006: 'The Google login dialog was closed before sign-in completed.',
      };

      closeWithError(errorMessageByCode[eventArg.error] || 'The Google login dialog failed.');
    });
  });
}

export async function signInWithGoogleOfficeDialog(): Promise<OfficeGoogleAuthSuccessPayload> {
  const payload = await openOfficeGoogleLoginDialog();
  if (payload.token) {
    setStoredOfficeAuthToken(payload.token);
  }
  dispatchOfficeAuthChanged();
  return payload;
}
